// Global Types
import type {
  $WithRouter,
} from '@vroskus/library-navigation';
import type {
  $Component,
} from 'common.types';
import type {
  $Translate,
} from 'common.language';

// Global Enums
import {
  adminPages,
  ErrorKey,
} from 'common.enums';
import {
  AdminAuthType,
} from 'common.data';

// Global Components
import * as React from 'react';
import {
  Button,
} from 'reactstrap';
import {
  AvForm,
  AvInput,
} from 'availity-reactstrap-validation';
import GoogleLogin from 'react-google-login';
import FacebookLogin from 'react-facebook-login';
import ReCAPTCHA from 'react-google-recaptcha';
import {
  Loader,
} from '@vroskus/library-components';

// Helpers
import _ from 'lodash';
import ErrorLog from '@vroskus/library-sentry/dist/browser';
import {
  animationClass,
  getUrlGetParams,
  makeCode,
} from '@vroskus/library-helpers/dist/browser';
import {
  CustomError,
} from '@vroskus/library-error';
import config from '../../../config';

// Types
import type {
  $ReducerState,
} from '../../../redux';
import type {
  $RedirectAction,
  $SetupMainDataAction,
  $SetupUserAction,
} from '../../../redux/modules/main/actions';
import type {
  $AuthenticateAction,
  $ForgotPasswordAction,
  $ResetPasswordAction,
} from '../../../redux/modules/auth/actions';

type $UserAuthType = AdminAuthType;

type $ErrorType = {
  defaultError: string;
  facebookError: string;
  googleError: string;
  invalidConfirmationCodeError: string;
  userEmailOccupiedError: string;
  userNotFoundError: string;
  userPasswordMismatchError: string;
};

const errorType: $ErrorType = {
  defaultError: 'DEFAULT_ERROR',
  facebookError: 'FACEBOOK_ERROR',
  googleError: 'GOOGLE_ERROR',
  invalidConfirmationCodeError: ErrorKey.invalidConfirmationCodeError,
  userEmailOccupiedError: ErrorKey.emailOccupiedError,
  userNotFoundError: ErrorKey.entityNotFoundError,
  userPasswordMismatchError: ErrorKey.passwordMismatchError,
};

export type $OwnProps = $Translate & $WithRouter;

export type $StateProps = {
  auth: $ReducerState<'auth'>;
};

export type $DispatchProps = {
  authenticate: $AuthenticateAction;
  forgotPassword: $ForgotPasswordAction;
  redirect: $RedirectAction;
  resetPassword: $ResetPasswordAction;
  setupMainData: $SetupMainDataAction;
  setupUser: $SetupUserAction;
};

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $Stage = 'forgot' | 'initial' | 'reset';

type $State = {
  activeErrorType: $ErrorType[keyof $ErrorType] | null;
  code: string;
  email: null | string;
  errorMessage: null | string;
  id: string;
  loading: boolean;
  recaptcha: boolean;
  stage: $Stage;
};

const userAuthType = AdminAuthType;

const pageId = adminPages.loginPage;

class LoginPageView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      activeErrorType: null,
      code: '',
      email: null,
      errorMessage: null,
      id: '',
      loading: false,
      recaptcha: false,
      stage: 'initial',
    };
  }

  componentDidMount(): void {
    this.init();
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  init(): void {
    const {
      auth,
      location,
      redirect,
    } = this.props;

    try {
      if (auth.user !== null) {
        const pathname: string = _.get(
          location,
          'state.from.pathname',
          '/',
        );

        redirect({
          pathname,
        });
      } else {
        const {
          code,
          id,
        } = getUrlGetParams(location.search);

        if (id && code) {
          this.setState({
            code,
            id,
            stage: 'reset',
          });
        }
      }
    } catch (err) {
      const error = err as Error;

      ErrorLog.exception(error);
    }
  }

  async authenticate(params: {
    token: string;
    type: $UserAuthType;
  }): Promise<void> {
    const {
      authenticate,
      location,
      redirect,
      setupMainData,
      setupUser,
    } = this.props;

    try {
      await this.setStateAsync({
        activeErrorType: null,
        errorMessage: null,
        loading: true,
      });

      const {
        value: user,
      } = await authenticate(params);

      if (user !== null) {
        await setupUser(user);

        const {
          regatta_id: RegattaId,
          region_season_id: RegionSeasonId,
        } = getUrlGetParams(location.search);

        await setupMainData({
          RegattaId,
          RegionSeasonId,
        });

        const pathname: string = _.get(
          location,
          'state.from.pathname',
          '/',
        );

        redirect({
          pathname,
          state: {
            successLogin: true,
          },
        });
      }
    } catch (error) {
      await this.setStateAsync({
        loading: false,
      });

      await this.failure(error as Error);
    }
  }

  async forgotPassword(params: {
    Email: string;
  }): Promise<void> {
    const {
      forgotPassword,
    } = this.props;

    try {
      const {
        value,
      } = await forgotPassword(params);

      await this.setStateAsync({
        activeErrorType: null,
        email: params.Email,
        id: value.id,
        stage: 'reset',
      });
    } catch (error) {
      await this.failure(error as Error);
    }
  }

  async resetPassword(params: {
    CnfPassword: string;
    Code: string;
    NewPassword: string;
  }): Promise<void> {
    const {
      resetPassword,
    } = this.props;
    const {
      id,
    } = this.state;

    try {
      const {
        value,
      } = await resetPassword({
        id,
        ...params,
      });

      await this.authenticate({
        token: btoa(JSON.stringify({
          email: value.Email,
          password: params.NewPassword,
        })),
        type: userAuthType.local,
      });
    } catch (error) {
      await this.failure(error as Error);
    }
  }

  async failure(error: Error): Promise<void> {
    const actions = {
      [errorType.defaultError]: async () => {
        await this.setStateAsync({
          activeErrorType: errorType.defaultError,
          errorMessage: null,
        });
      },
      [errorType.facebookError]: async () => {
        await this.setStateAsync({
          activeErrorType: errorType.facebookError,
          errorMessage: error.message,
        });
      },
      [errorType.googleError]: async () => {
        await this.setStateAsync({
          activeErrorType: errorType.googleError,
          errorMessage: error.message,
        });
      },
      [errorType.invalidConfirmationCodeError]: async () => {
        await this.setStateAsync({
          activeErrorType: errorType.invalidConfirmationCodeError,
          errorMessage: null,
        });
      },
      [errorType.userEmailOccupiedError]: async () => {
        await this.setStateAsync({
          activeErrorType: errorType.userEmailOccupiedError,
          errorMessage: null,
        });
      },
      [errorType.userNotFoundError]: async () => {
        await this.setStateAsync({
          activeErrorType: errorType.userNotFoundError,
          errorMessage: null,
        });
      },
      [errorType.userPasswordMismatchError]: async () => {
        await this.setStateAsync({
          activeErrorType: errorType.userPasswordMismatchError,
          errorMessage: null,
        });
      },
    };

    const responseKey: $ErrorType[keyof $ErrorType] | undefined = _.get(
      error,
      'response.data.key',
    );

    const action = typeof responseKey !== 'undefined' ? _.get(
      actions,
      responseKey,
      actions[errorType.defaultError],
    ) : actions[errorType.defaultError];

    await action();

    ErrorLog.exception(error as Error);
  }

  renderGoogleButton() {
    const {
      t,
    } = this.props;

    return (config.AUTH_GOOGLE_CLIENT_ID !== '' ? (
      <GoogleLogin
        clientId={config.AUTH_GOOGLE_CLIENT_ID}
        cookiePolicy={'single_host_origin'}
        icon={false}
        onFailure={async (error) => {
          const errorMessage: string = _.get(
            error,
            'details',
            error ? makeCode(JSON.stringify(error)) : 'Unknown message',
          );

          const customError = new CustomError(
            errorMessage,
            ErrorKey.googleError,
            {
              data: {
                error,
              },
            },
          );

          await this.failure(customError);
        }}
        onSuccess={async (response) => {
          const token: string | undefined = _.get(
            response,
            'accessToken',
          );

          if (typeof token === 'undefined') {
            const customError = new CustomError(
              'AccessToken not provided',
              ErrorKey.googleError,
              {
                data: {
                  response,
                },
              },
            );

            ErrorLog.exception(customError);

            return;
          }

          this.authenticate({
            token,
            type: userAuthType.google,
          });
        }}
        render={(renderProps) => (
          <button
            className={'btn btn-block btn-secondary btn-google mb-3'}
            onClick={renderProps.onClick}
            type={'button'}
          >
            <em className={'mr-2 fab fa-google'} />
            {t('Sign in with Google')}
          </button>
        )}
      />
    ) : null);
  }

  renderFacebookButton() {
    const {
      t,
    } = this.props;

    return (config.AUTH_FACEBOOK_APP_ID !== '' ? (
      <FacebookLogin
        appId={config.AUTH_FACEBOOK_APP_ID}
        callback={async (response) => this.authenticate({
          token: response.accessToken,
          type: userAuthType.facebook,
        })}
        cssClass={'btn btn-block btn-secondary btn-facebook mb-3'}
        disableMobileRedirect
        fields={'name,email,picture'}
        icon={(
          <em className={'mr-2 fab fa-facebook'} />
        )}
        isMobile={false}
        onFailure={async (error) => {
          const errorMessage: string = _.get(
            error,
            'details',
            error ? makeCode(JSON.stringify(error)) : 'Unknown message',
          );

          const customError = new CustomError(
            errorMessage,
            ErrorKey.facebookError,
            {
              data: {
                error,
              },
            },
          );

          await this.failure(customError);
        }}
        scope={'public_profile, email'}
        textButton={t('Sign in with Facebook')}
      />
    ) : null);
  }

  renderSocial() {
    return (
      <>
        {this.renderGoogleButton()}
        {this.renderFacebookButton()}
        <hr className={'mb-3'} />
      </>
    );
  }

  renderRecapcha() {
    return (config.RECAPCHA_SITE_KEY !== '' ? (
      <div className={'mb-3'}>
        <ReCAPTCHA
          onChange={() => this.setState({
            recaptcha: true,
          })}
          sitekey={config.RECAPCHA_SITE_KEY}
        />
      </div>
    ) : null);
  }

  renderInitialStage() {
    const {
      t,
    } = this.props;
    const {
      recaptcha,
    } = this.state;

    return (
      <>
        <p className={'text-center mb-2'}>
          {t('Sign in to continue')}
        </p>
        {this.renderSocial()}
        <AvForm onValidSubmit={(event: Event, {
          email,
          password,
        }) => this.authenticate({
          token: btoa(JSON.stringify({
            email,
            password,
          })),
          type: userAuthType.local,
        })}
        >
          <div className={'form-group mb-3'}>
            <div className={'input-group with-focus'}>
              <AvInput
                autoComplete={'email'}
                key={'email'}
                label={t('Email')}
                name={'email'}
                placeholder={t('Enter email')}
                required
                type={'email'}
              />
              <div className={'input-group-append'}>
                <span className={'input-group-text text-muted bg-transparent border-left-0'}>
                  <em className={'fa fa-envelope'} />
                </span>
              </div>
            </div>
          </div>
          <div className={'form-group mb-3'}>
            <div className={'input-group with-focus'}>
              <AvInput
                autoComplete={'off'}
                key={'password'}
                label={t('Password')}
                name={'password'}
                placeholder={t('Enter password')}
                required
                type={'password'}
              />
              <div className={'input-group-append'}>
                <span className={'input-group-text text-muted bg-transparent border-left-0'}>
                  <em className={'fa fa-lock'} />
                </span>
              </div>
            </div>
          </div>
          {this.renderRecapcha()}
          <Button
            block
            className={'mb-3'}
            color={'success'}
            disabled={config.RECAPCHA_SITE_KEY !== '' && !recaptcha}
            id={'login-button'}
            type={'submit'}
          >
            {t('Login')}
          </Button>
        </AvForm>
        <button
          className={'border-0 bg-white text-muted'}
          onClick={() => this.setState({
            activeErrorType: null,
            recaptcha: false,
            stage: 'forgot',
          })}
          type={'button'}
        >
          {t('Forgot password?')}
        </button>
      </>
    );
  }

  renderForgotStage() {
    const {
      t,
    } = this.props;
    const {
      recaptcha,
    } = this.state;

    return (
      <>
        <p className={'text-center mb-2'}>
          {t('Input your account email')}
        </p>
        <AvForm onValidSubmit={(event: Event, params) => this.forgotPassword(params)}>
          <div className={'form-group mb-3'}>
            <div className={'input-group with-focus'}>
              <AvInput
                autoComplete={'email'}
                key={'email'}
                label={t('Email')}
                name={'Email'}
                placeholder={t('Enter email')}
                required
                type={'email'}
              />
              <div className={'input-group-append'}>
                <span className={'input-group-text text-muted bg-transparent border-left-0'}>
                  <em className={'fa fa-envelope'} />
                </span>
              </div>
            </div>
          </div>
          <div className={'mb-3'}>
            {this.renderRecapcha()}
          </div>
          <Button
            block
            className={'mb-3'}
            color={'warning'}
            disabled={!recaptcha}
            id={'send-reset-link-button'}
            type={'submit'}
          >
            {t('Send reset link')}
          </Button>
        </AvForm>
        <button
          className={'border-0 bg-white text-muted'}
          onClick={() => this.setState({
            activeErrorType: null,
            recaptcha: false,
            stage: 'initial',
          })}
          type={'button'}
        >
          {t('Back to login form')}
        </button>
      </>
    );
  }

  renderResetStage() {
    const {
      t,
    } = this.props;
    const {
      code,
      email,
    } = this.state;

    return (
      <>
        <p className={'text-center mb-2'}>
          {t('Input new password')}
        </p>
        <AvForm onValidSubmit={(event: Event, params) => this.resetPassword(params)}>
          <div className={'form-group mb-3'}>
            <div className={'input-group with-focus'}>
              <AvInput
                autoComplete={'off'}
                key={'newPassword'}
                label={t('New password')}
                name={'NewPassword'}
                placeholder={t('Enter new password')}
                required
                type={'password'}
              />
              <div className={'input-group-append'}>
                <span className={'input-group-text text-muted bg-transparent border-left-0'}>
                  <em className={'fa fa-lock'} />
                </span>
              </div>
            </div>
          </div>
          <div className={'form-group mb-3'}>
            <div className={'input-group'}>
              <AvInput
                autoComplete={'off'}
                key={'cnfPassword'}
                label={t('Repeat password')}
                name={'CnfPassword'}
                placeholder={t('Repeat new password')}
                required
                type={'password'}
              />
              <div className={'input-group-append'}>
                <span className={'input-group-text text-muted bg-transparent border-left-0'}>
                  <em className={'fa fa-lock'} />
                </span>
              </div>
            </div>
          </div>
          <div className={'form-group mb-3'}>
            <div className={'input-group mb-1'}>
              <AvInput
                key={'code'}
                label={t('Confirmation code')}
                name={'Code'}
                placeholder={t('Enter confirmation code')}
                required
                type={'text'}
                value={code}
              />
            </div>
            <p className={'text-center text-muted'}>
              {t('Confirmation code is sent to your email')}
            </p>
          </div>
          <Button
            block
            className={'mb-3'}
            color={'warning'}
            id={'reset-password-button'}
            type={'submit'}
          >
            {t('Reset password')}
          </Button>
        </AvForm>
        {email !== null && (
          <button
            className={'border-0 bg-white text-muted'}
            onClick={async () => {
              await this.forgotPassword({
                Email: email,
              });

              await this.setStateAsync({
                email: null,
              });
            }}
            type={'button'}
          >
            {t('Resend confirmation code')}
          </button>
        )}
      </>
    );
  }

  renderStage() {
    const {
      stage,
    } = this.state;

    const stages: Record<$Stage, () => $Component<unknown>> = {
      forgot: () => this.renderForgotStage(),
      initial: () => this.renderInitialStage(),
      reset: () => this.renderResetStage(),
    };

    return stages[stage]();
  }

  renderError() {
    const {
      t,
    } = this.props;
    const {
      activeErrorType,
      errorMessage,
    } = this.state;

    if (activeErrorType !== null) {
      const defaultError = () => errorMessage || t('Error');

      const errors: Record<$ErrorType[keyof $ErrorType], () => string> = {
        [errorType.defaultError]: defaultError,
        [errorType.invalidConfirmationCodeError]: () => t('Incorrect code'),
        [errorType.userEmailOccupiedError]: () => t('Email already occupied'),
        [errorType.userNotFoundError]: () => t('Email not found'),
        [errorType.userPasswordMismatchError]: () => t('Incorrect password'),
      };

      return (
        <p className={'text-center text-danger'}>
          {errors[activeErrorType]()}
        </p>
      );
    }

    return null;
  }

  render() {
    const {
      loading,
    } = this.state;

    return (
      <div
        className={'block-center mt-4 wd-xl h-100'}
        id={pageId}
        style={{
          minWidth: 332,
        }}
      >
        <div className={`${animationClass('fadeInUp')} card card-flat`}>
          <div className={'card-header text-center'}>
            <img
              alt={'Logo'}
              className={'block-center rounded'}
              src={'/img/logo_d.png'}
            />
          </div>
          <hr className={'m-0'} />
          <div className={'card-body text-center'}>
            <Loader
              loading={loading === true}
              overlay
            >
              {this.renderStage()}
            </Loader>
          </div>
          {this.renderError()}
        </div>
      </div>
    );
  }
}

export default LoginPageView;
